/**
 * Confirm (typed-name for published) / deleting / failed states. Shared by
 * CalendarsPage and AlertsPage — was two near-identical copies
 * (CalendarsPage/DeleteModal.jsx, AlertsPage/DeleteModal.jsx), differing
 * only by the "calendar"/"alert" noun. Success isn't handled here: the modal
 * just closes and the page fires the toast once its own deleteSuccess flips.
 */
import React, { useEffect, useState } from "react";
import ConfirmDialog from "../../theme/untitled/ConfirmDialog/ConfirmDialog";
import Input from "../../theme/untitled/Input/Input";
import Icon from "../../theme/untitled/icons/Icon";
import styles from "./DeleteModal.module.scss";

const DeleteModal = ({ item, itemLabel, deleting, deleteErrorMessage, onConfirm, onCancel }) => {
  const [typedName, setTypedName] = useState("");

  // Reset the confirm field whenever a new item's modal opens.
  useEffect(() => {
    setTypedName("");
  }, [item?.uuid]);

  if (!item) {
    return null;
  }

  const requiresTyping = item.available === 1;
  const nameMatches = typedName === item.name;
  const canConfirm = !requiresTyping || nameMatches;

  return (
    <ConfirmDialog
      open
      onClose={onCancel}
      onConfirm={() => onConfirm(item)}
      icon={<Icon name="Trash01" />}
      title={`Delete "${item.name}"?`}
      panelClassName={styles.panel}
      confirmLabel={deleting ? "Deleting…" : deleteErrorMessage ? "Try again" : `Delete ${itemLabel}`}
      confirmDisabled={!canConfirm}
      busy={deleting}
    >
      <div className={styles.body}>
        <p>
          {requiresTyping
            ? "Wherever this is embedded, it will stop working and show an error to visitors instead."
            : `This ${itemLabel} isn’t published, so nothing changes for site visitors.`}
        </p>
        <p>Its source events aren&rsquo;t affected.</p>
        <p>This cannot be undone.</p>
        {requiresTyping && (
          <div className={styles.confirmField}>
            <Input
              size="sm"
              label={`Type "${item.name}" to confirm`}
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              disabled={deleting}
              autoComplete="off"
            />
          </div>
        )}
        {deleteErrorMessage && (
          <div className={styles.failureBlock}>
            <Icon name="AlertTriangle" className={styles.failureIcon} />
            <span className={styles.failureText}>
              Nothing was deleted &mdash; the embed is still live. {deleteErrorMessage}
            </span>
          </div>
        )}
      </div>
    </ConfirmDialog>
  );
};

export default DeleteModal;
